import React from "react";
import { TrendingUp, Timer, GitMerge, Waves, ChevronRight, Info } from "lucide-react";
import { RISK_WEIGHTS } from "../services/riskEngine";

/* Risk horizon — the anticipatory view.
 *
 * Deliberately leads with LEAD TIME rather than current status. "Ramkund is
 * 92% full" is something an operator already knows from the map; "Ramkund
 * reaches critical in 23 minutes at the current rate" is the sentence that
 * changes what they do in the next five minutes.
 *
 * Every score is decomposable: the drivers are listed under it, because a
 * risk number an operator cannot take apart is one they can neither act on
 * with confidence nor defend at an inquiry afterwards. */
export default function RiskHorizonPanel({ lang = "en", horizon, onFocus }) {
  const L = (o) => o?.[lang] ?? o?.en ?? "";
  const { zones, bottlenecks, accumulation, abnormal, soonest, assumptions } = horizon;

  const bandCls = {
    red: "bg-red-50 border-red-300",
    amber: "bg-amber-50 border-amber-300",
    green: "bg-emerald-50 border-emerald-200",
  };
  const bandText = { red: "text-red-700", amber: "text-amber-700", green: "text-emerald-700" };

  return (
    <div className="border-t border-slate-200">
      <section className="px-3 py-3">
        <div className="flex items-center gap-1.5 mb-2">
          <TrendingUp size={12} className="text-slate-500" />
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
            {lang === "mr" ? "जोखीम क्षितिज" : "Risk Horizon"}
          </span>
          <span className="ml-auto text-[8px] font-black px-1.5 py-0.5 rounded border bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300">
            {lang === "mr" ? "प्रक्षेपित" : "PROJECTED"}
          </span>
        </div>

        {/* The headline: soonest failure. */}
        {soonest ? (
          <button
            type="button"
            onClick={() => onFocus({ coord: soonest.centre, zoom: 14 })}
            className="w-full text-left rounded-lg border-2 border-red-300 bg-red-50 p-2.5 hover:border-red-400 transition-colors"
          >
            <div className="flex items-center gap-1.5">
              <Timer size={13} className="text-red-700" />
              <span className="text-[10px] font-black uppercase tracking-wider text-red-700">
                {lang === "mr" ? "सर्वात आधी गंभीर" : "Breaks first"}
              </span>
            </div>
            <div className="mt-1 text-[15px] font-black text-red-900 leading-none tabular-nums">
              {soonest.minutesToCritical} {lang === "mr" ? "मिनिटे" : "min"}
            </div>
            <div className="text-[11px] text-red-800 mt-0.5">
              {L(soonest.name)} · {soonest.occupancyPct}% ·{" "}
              {soonest.netPerMin > 0 ? `+${soonest.netPerMin}` : soonest.netPerMin}/min
            </div>
          </button>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-200 px-2 py-3 text-center text-[11px] text-slate-400">
            {lang === "mr"
              ? "सध्या कोणताही झोन गंभीर होण्याच्या मार्गावर नाही"
              : "No zone is on a path to critical at current rates"}
          </div>
        )}

        {/* Ranked composite risk */}
        <div className="mt-2 space-y-1.5">
          {zones.slice(0, 4).map((z) => (
            <button
              key={z.sectorId}
              type="button"
              onClick={() => onFocus({ coord: z.centre, zoom: 14 })}
              className={`w-full text-left rounded-lg border p-2 transition-colors hover:brightness-95 ${bandCls[z.band]}`}
            >
              <div className="flex items-center gap-2">
                <span className={`text-[13px] font-black tabular-nums ${bandText[z.band]}`}>
                  {z.score}
                </span>
                <span className="text-[11px] font-bold text-slate-800 truncate flex-1">
                  {L(z.name)}
                </span>
                {z.minutesToCritical != null && z.minutesToCritical > 0 && (
                  <span className="text-[9px] font-bold text-slate-500 tabular-nums whitespace-nowrap">
                    T−{z.minutesToCritical}m
                  </span>
                )}
              </div>
              {z.drivers.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {z.drivers.map((d) => (
                    <li key={d.k} className="text-[9px] leading-snug text-slate-600">
                      · {d.text}
                    </li>
                  ))}
                </ul>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Bottlenecks */}
      {bottlenecks.length > 0 && (
        <section className="px-3 py-3 border-t border-slate-200">
          <Head icon={GitMerge}>{lang === "mr" ? "अडथळे" : "Bottlenecks"}</Head>
          {bottlenecks.map((b) => (
            <button
              key={b.sectorId}
              type="button"
              onClick={() => onFocus({ coord: b.centre, zoom: 14.5 })}
              className="w-full text-left rounded-lg border border-orange-300 bg-orange-50 p-2 mb-1.5 hover:border-orange-400"
            >
              <div className="text-[11px] font-bold text-orange-900">{L(b.name)}</div>
              <div className="text-[9px] text-orange-800 mt-0.5">
                {b.inPerMin}/min {lang === "mr" ? "येत आहेत" : "arriving"} ·{" "}
                {b.entryGates} {lang === "mr" ? "द्वारे" : "gate(s)"} ={" "}
                {b.entryCapacityPerMin}/min {lang === "mr" ? "क्षमता" : "capacity"}
              </div>
              <div className="text-[10px] font-black text-orange-900 mt-0.5">
                {b.demandRatio}× {lang === "mr" ? "क्षमतेपेक्षा जास्त" : "over capacity"}
              </div>
            </button>
          ))}
        </section>
      )}

      {/* Accumulation */}
      {accumulation.length > 0 && (
        <section className="px-3 py-3 border-t border-slate-200">
          <Head icon={Waves}>{lang === "mr" ? "गर्दी साचणे" : "Accumulation"}</Head>
          {accumulation.slice(0, 3).map((a) => (
            <div key={a.sectorId} className="flex items-center justify-between text-[10px] mb-1">
              <span className="text-slate-700 truncate">{L(a.name)}</span>
              <span className="font-bold text-slate-900 tabular-nums whitespace-nowrap">
                +{a.pctPerHour}%/hr
              </span>
            </div>
          ))}
        </section>
      )}

      {/* Counter-flow */}
      {abnormal.length > 0 && (
        <section className="px-3 py-3 border-t border-slate-200">
          <Head icon={Info}>{lang === "mr" ? "प्रतिप्रवाह" : "Counter-flow"}</Head>
          {abnormal.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onFocus({ coord: a.coord, zoom: 15 })}
              className="w-full text-left text-[10px] text-slate-700 mb-1 hover:text-slate-900"
            >
              {a.sectorId} — {a.deviationDeg}°{" "}
              {lang === "mr" ? "मुख्य प्रवाहाविरुद्ध" : "against dominant flow"}
            </button>
          ))}
        </section>
      )}

      {/* Method — always available, never hidden behind a hover. */}
      <section className="px-3 pb-3">
        <details>
          <summary className="text-[9px] font-bold text-slate-400 cursor-pointer hover:text-slate-600 flex items-center gap-0.5">
            <ChevronRight size={8} />
            {lang === "mr" ? "पद्धत व गृहीतके" : "Method & assumptions"}
          </summary>
          <div className="mt-1 text-[9px] text-slate-500 leading-snug">
            <div className="font-bold text-slate-600 mb-0.5">
              {lang === "mr" ? "गुण भार" : "Score weights"}:{" "}
              {Object.entries(RISK_WEIGHTS).map(([k, v]) => `${k} ${v}`).join(" · ")}
            </div>
            <ul className="space-y-0.5">
              {assumptions.map((a) => <li key={a}>· {a}</li>)}
            </ul>
          </div>
        </details>
      </section>
    </div>
  );
}

function Head({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon size={11} className="text-slate-400" />
      <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{children}</span>
    </div>
  );
}
